import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClient } from 'urql'
import {
  PRIVACY_SUBJECT_SEARCH_PREPARE,
  PRIVACY_SUBJECT_SEARCH_STEP,
} from '../../graphql/privacy'
import './PrivacySubjectSearch.css'

const EMPTY_FORM = {
  name: '',
  email: '',
  userId: '',
}

const ALL_COMPANIES = '__ALL__'

const CATEGORY_ORDER = [
  'access',
  'membership',
  'contacts',
  'credentials',
  'integrations',
  'automation',
  'sensitive',
  'residual',
]

const CATEGORY_LABEL = {
  access: 'Acceso al portal y sesiones',
  membership: 'Membresía, roles y equipos',
  contacts: 'Contactos y continuidad operacional',
  credentials: 'Credenciales personales',
  integrations: 'Integraciones externas',
  automation: 'Automatizaciones e IA',
  sensitive: 'Información en AWS S3',
  residual: 'Residuos y evidencia',
}

const MATCH_REASON_LABEL = {
  rut: 'RUT de la empresa',
  membership: 'Miembro o admin de la empresa',
  email: 'Correo asociado',
  activity: 'Actividad del usuario (descargas, IA, etc.)',
  userId: 'ID del usuario en Mongo',
  name: 'Nombre en el portal',
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function hasAnyField(form) {
  return Boolean(
    String(form.name || '').trim() ||
      String(form.email || '').trim() ||
      String(form.userId || '').trim(),
  )
}

function hitLogMessage(hit) {
  const where = hit.collection || hit.externalSystem || hit.system
  if (hit.skippedReason) {
    return `${hit.label} (${where}): omitido — ${hit.skippedReason}`
  }
  if (hit.count == null) {
    return `${hit.label}: revisar manualmente`
  }
  if (hit.count === 0) {
    return `${hit.label} (${where}): sin hallazgos`
  }
  return `${hit.label} (${where}): ${hit.count} hallazgo${hit.count === 1 ? '' : 's'}`
}

function sliceHitForCompany(hit, companyId) {
  if (!companyId || companyId === ALL_COMPANIES) return hit

  if (hit.byCompany?.length) {
    const slice = hit.byCompany.find((b) => b.companyId === companyId)
    if (!slice || slice.count === 0) {
      if (hit.count == null && !hit.skippedReason) return hit
      return null
    }
    return {
      ...hit,
      count: slice.count,
      samples: slice.samples || [],
    }
  }

  const samples = hit.samples || []
  const withCompany = samples.filter((s) => s.companyId)
  if (!withCompany.length) return hit

  const filtered = samples.filter((s) => s.companyId === companyId)
  if (!filtered.length) {
    if (hit.count == null) return hit
    return null
  }
  return {
    ...hit,
    samples: filtered,
    count: hit.count != null ? filtered.length : hit.count,
  }
}

/** true = hay algo que el operador debe atender en offboarding. */
function needsAttention(hit) {
  if (hit?.skippedReason) return false
  if (hit?.count == null) return true // declarativo: revisar
  return hit.count > 0
}

function statusOf(hit, resolved) {
  if (resolved) return 'done'
  if (!needsAttention(hit)) return 'clear'
  if (hit.count == null) return 'review'
  if (hit.severity === 'blocking') return 'blocking'
  if (hit.severity === 'warning') return 'warning'
  return 'open'
}

const STATUS_LABEL = {
  done: 'Resuelto',
  clear: 'Sin hallazgo',
  review: 'Revisar',
  blocking: 'Bloqueante',
  warning: 'Atención',
  open: 'Pendiente',
}

const STATUS_SORT = {
  blocking: 0,
  warning: 1,
  open: 2,
  review: 3,
  done: 4,
  clear: 5,
}

function parseSampleParts(label) {
  return String(label || '')
    .split(' · ')
    .map((p) => p.trim())
    .filter(Boolean)
}

/** Separa bucket (contexto) vs key a borrar (resaltada). */
function splitS3Uri(uri) {
  const raw = String(uri || '')
  const match = raw.match(/^(s3:\/\/[^/]+\/)(.+)$/)
  if (!match) return { prefix: raw, removable: '' }
  return { prefix: match[1], removable: match[2] }
}

function S3PathUri({ uri }) {
  const { prefix, removable } = splitS3Uri(uri)
  if (!removable) {
    return (
      <code className="privacy-subject-s3-paths-uri" title="Seleccionar y copiar">
        {uri}
      </code>
    )
  }
  return (
    <code
      className="privacy-subject-s3-paths-uri"
      title="En rojo: ruta o prefijo a eliminar en S3"
    >
      <span className="privacy-subject-s3-paths-keep">{prefix}</span>
      <span className="privacy-subject-s3-paths-remove">{removable}</span>
    </code>
  )
}

/**
 * A-09 — Checklist de baja de usuario × empresa.
 */
export function PrivacySubjectSearch({ onCreateCase }) {
  const client = useClient()
  const abortRef = useRef(0)
  const logEndRef = useRef(null)

  const [form, setForm] = useState(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState('')
  const [plan, setPlan] = useState(null)
  const [selectedCompanyId, setSelectedCompanyId] = useState(ALL_COMPANIES)
  const [progress, setProgress] = useState(null)
  const [logs, setLogs] = useState([])
  const [hits, setHits] = useState([])
  const [scanDone, setScanDone] = useState(false)
  const [resolvedIds, setResolvedIds] = useState(() => new Set())
  const [resolveConfirm, setResolveConfirm] = useState(null)
  /** true = prepare encontró varios users; esperar selección antes del scan. */
  const [awaitingUserPick, setAwaitingUserPick] = useState(false)
  const [pickedUserId, setPickedUserId] = useState('')

  const pushLog = useCallback((message, tone = 'info') => {
    setLogs((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, message, tone, at: new Date() },
    ])
  }, [])

  const setName = useCallback((event) => {
    setForm((prev) => ({ ...prev, name: event.target.value }))
  }, [])

  const setEmail = useCallback((event) => {
    setForm((prev) => ({ ...prev, email: event.target.value }))
  }, [])

  const setUserId = useCallback((event) => {
    setForm((prev) => ({ ...prev, userId: event.target.value.trim() }))
  }, [])

  const buildInput = useCallback(() => {
    const input = {}
    const name = String(form.name || '').trim()
    const email = String(form.email || '').trim()
    const userId = String(form.userId || '').trim()
    if (name) input.name = name
    if (email) input.email = email
    if (userId) input.userId = userId
    return input
  }, [form])

  const runScan = useCallback(
    async (activePlan) => {
      if (!activePlan?.searchId) return

      setHits([])
      setLogs([])
      setProgress(null)
      setScanDone(false)
      setResolvedIds(new Set())
      setResolveConfirm(null)

      const runId = abortRef.current + 1
      abortRef.current = runId
      setBusy(true)

      const liveSteps = (activePlan.steps || []).filter((s) => s.live)
      const declarativeSteps = (activePlan.steps || []).filter((s) => !s.live)

      try {
        pushLog(
          activePlan.companies?.length
            ? `Evaluando baja en ${activePlan.companies.length} empresa(s)…`
            : 'Evaluando fuentes del usuario (sin empresa anclada)…',
        )
        setProgress({ current: 0, total: liveSteps.length, label: 'Iniciando…' })

        const collected = []

        for (let i = 0; i < liveSteps.length; i += 1) {
          if (abortRef.current !== runId) return
          const step = liveSteps[i]
          const where = step.collection || step.label
          setProgress({
            current: i,
            total: liveSteps.length,
            label: `Revisando ${where}…`,
          })
          pushLog(
            `Revisando ${step.label}${step.collection ? ` → ${step.collection}` : ''}…`,
          )

          const stepped = await client
            .query(
              PRIVACY_SUBJECT_SEARCH_STEP,
              {
                input: {
                  searchId: activePlan.searchId,
                  sourceId: step.sourceId,
                },
              },
              { requestPolicy: 'network-only' },
            )
            .toPromise()

          if (abortRef.current !== runId) return

          if (stepped.error) {
            const msg = stepped.error.message.replace('[GraphQL] ', '')
            pushLog(`Error en ${step.label}: ${msg}`, 'error')
            collected.push({
              sourceId: step.sourceId,
              label: step.label,
              layer: step.layer,
              system: step.system,
              collection: step.collection,
              externalSystem: step.externalSystem,
              offboardingCategory: step.offboardingCategory,
              severity: step.severity,
              count: null,
              samples: [],
              skippedReason: msg,
            })
            setHits([...collected])
            setProgress({ current: i + 1, total: liveSteps.length, label: where })
            continue
          }

          const hit = stepped.data?.privacySubjectSearchStep?.hit
          if (hit) {
            collected.push(hit)
            pushLog(hitLogMessage(hit), hit.count > 0 ? 'ok' : 'info')
            setHits([...collected])
          }
          setProgress({ current: i + 1, total: liveSteps.length, label: where })
        }

        for (const step of declarativeSteps) {
          if (abortRef.current !== runId) return
          const fromPrepare = (activePlan.declarativeHits || []).find(
            (h) => h.sourceId === step.sourceId,
          )
          // Sin hit del prepare = no hay objetos/casos que revisar (p. ej. S3 vacío).
          if (!fromPrepare) continue
          collected.push(fromPrepare)
          pushLog(hitLogMessage(fromPrepare), 'info')
        }
        setHits([...collected])
        setProgress({
          current: liveSteps.length,
          total: liveSteps.length,
          label: 'Listo',
        })
        pushLog('Checklist generado', 'ok')
        setScanDone(true)
      } catch (err) {
        if (abortRef.current !== runId) return
        const msg = err?.message || 'No se pudo completar la evaluación'
        setLocalError(msg)
        pushLog(msg, 'error')
      } finally {
        if (abortRef.current === runId) setBusy(false)
      }
    },
    [client, pushLog],
  )

  const runSearch = useCallback(
    async (event) => {
      event?.preventDefault?.()
      setLocalError('')
      setPlan(null)
      setSelectedCompanyId(ALL_COMPANIES)
      setHits([])
      setLogs([])
      setProgress(null)
      setScanDone(false)
      setResolvedIds(new Set())
      setResolveConfirm(null)
      setAwaitingUserPick(false)
      setPickedUserId('')

      if (!hasAnyField(form)) {
        setLocalError('Indica correo, nombre o userId del usuario a dar de baja.')
        return
      }

      const runId = abortRef.current + 1
      abortRef.current = runId
      setBusy(true)

      try {
        pushLog('Identificando usuario…')
        const prepared = await client
          .query(
            PRIVACY_SUBJECT_SEARCH_PREPARE,
            { input: buildInput() },
            { requestPolicy: 'network-only' },
          )
          .toPromise()

        if (abortRef.current !== runId) return

        if (prepared.error) {
          setLocalError(prepared.error.message.replace('[GraphQL] ', ''))
          setBusy(false)
          return
        }

        const nextPlan = prepared.data?.privacySubjectSearchPrepare
        if (!nextPlan) {
          setLocalError('No se pudo preparar la evaluación')
          setBusy(false)
          return
        }

        setPlan(nextPlan)
        setSelectedCompanyId(ALL_COMPANIES)
        setBusy(false)

        const matchedUsers = nextPlan.users || []
        const hasIdentityUser = Boolean(nextPlan.subject?.userId)

        if (!matchedUsers.length) {
          setLocalError(
            'No hay usuario en users para este criterio. Revisa el correo/nombre o busca por ObjectId.',
          )
          pushLog(
            'Sin documento de usuario: no se puede evaluar la baja de una cuenta.',
            'warn',
          )
          return
        }

        // Varios matches, o candidatos solo por membresía de empresa (sin subject.userId).
        if (matchedUsers.length > 1 || !hasIdentityUser) {
          setAwaitingUserPick(true)
          setPickedUserId(matchedUsers[0].id || '')
          pushLog(
            !hasIdentityUser
              ? `El criterio no resolvió un users._id; hay ${matchedUsers.length} usuario(s) en empresas vinculadas. Elige cuál revisar.`
              : `Se encontraron ${matchedUsers.length} usuarios. Elige cuál revisar.`,
            'info',
          )
          return
        }

        setPickedUserId(matchedUsers[0].id || '')
        await runScan(nextPlan)
      } catch (err) {
        if (abortRef.current !== runId) return
        setLocalError(err?.message || 'No se pudo preparar la evaluación')
        setBusy(false)
      }
    },
    [client, form, buildInput, pushLog, runScan],
  )

  const confirmPickedUser = useCallback(async () => {
    if (!pickedUserId) {
      setLocalError('Selecciona un usuario para continuar.')
      return
    }

    setLocalError('')
    setAwaitingUserPick(false)
    setHits([])
    setLogs([])
    setProgress(null)
    setScanDone(false)
    setResolvedIds(new Set())
    setResolveConfirm(null)

    const runId = abortRef.current + 1
    abortRef.current = runId
    setBusy(true)

    try {
      pushLog(`Acotando evaluación al usuario ${pickedUserId}…`)
      const prepared = await client
        .query(
          PRIVACY_SUBJECT_SEARCH_PREPARE,
          { input: { userId: pickedUserId } },
          { requestPolicy: 'network-only' },
        )
        .toPromise()

      if (abortRef.current !== runId) return

      if (prepared.error) {
        setLocalError(prepared.error.message.replace('[GraphQL] ', ''))
        setBusy(false)
        setAwaitingUserPick(true)
        return
      }

      const nextPlan = prepared.data?.privacySubjectSearchPrepare
      if (!nextPlan) {
        setLocalError('No se pudo preparar la evaluación del usuario elegido')
        setBusy(false)
        setAwaitingUserPick(true)
        return
      }

      setPlan(nextPlan)
      setSelectedCompanyId(ALL_COMPANIES)
      setPickedUserId(nextPlan.subject?.userId || pickedUserId)
      setBusy(false)
      await runScan(nextPlan)
    } catch (err) {
      if (abortRef.current !== runId) return
      setLocalError(err?.message || 'No se pudo acotar al usuario elegido')
      setBusy(false)
      setAwaitingUserPick(true)
    }
  }, [pickedUserId, client, pushLog, runScan])

  const filteredHits = useMemo(
    () =>
      hits
        .map((hit) => sliceHitForCompany(hit, selectedCompanyId))
        .filter(Boolean),
    [hits, selectedCompanyId],
  )

  const checklistItems = useMemo(
    () => filteredHits.filter((hit) => needsAttention(hit)),
    [filteredHits],
  )

  const openItems = useMemo(
    () =>
      checklistItems.filter(
        (hit) => needsAttention(hit) && !resolvedIds.has(hit.sourceId),
      ),
    [checklistItems, resolvedIds],
  )

  const blockingOpen = useMemo(
    () => openItems.filter((hit) => hit.severity === 'blocking' || hit.count == null),
    [openItems],
  )

  const companyNameById = useMemo(() => {
    const map = {}
    for (const c of plan?.companies || []) {
      map[c.id] = c.name || c.rut || c.id
    }
    return map
  }, [plan])

  const byCategory = useMemo(() => {
    const map = {}
    for (const hit of checklistItems) {
      const key = hit.offboardingCategory || 'residual'
      if (!map[key]) map[key] = []
      map[key].push(hit)
    }
    return CATEGORY_ORDER.filter((k) => map[k]?.length).map((k) => {
      const rows = [...map[k]].sort((a, b) => {
        const sa = statusOf(a, resolvedIds.has(a.sourceId))
        const sb = statusOf(b, resolvedIds.has(b.sourceId))
        return (STATUS_SORT[sa] ?? 9) - (STATUS_SORT[sb] ?? 9)
      })
      const openCount = rows.filter(
        (h) => needsAttention(h) && !resolvedIds.has(h.sourceId),
      ).length
      return {
        key: k,
        title: CATEGORY_LABEL[k] || k,
        rows,
        openCount,
        total: rows.length,
      }
    })
  }, [checklistItems, resolvedIds])

  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId || selectedCompanyId === ALL_COMPANIES) return null
    return (plan?.companies || []).find((c) => c.id === selectedCompanyId) || null
  }, [plan, selectedCompanyId])

  const scopeLabel = useMemo(() => {
    if (selectedCompany) return selectedCompany.name || selectedCompany.id
    if (plan?.companies?.length) return `todas las empresas (${plan.companies.length})`
    return null
  }, [selectedCompany, plan])

  const canOffboard = scanDone && openItems.length === 0

  const percent = progress?.total
    ? Math.min(100, Math.round((progress.current / progress.total) * 100))
    : 0

  const toggleResolved = useCallback((sourceId) => {
    setResolvedIds((prev) => {
      const next = new Set(prev)
      if (next.has(sourceId)) next.delete(sourceId)
      else next.add(sourceId)
      return next
    })
  }, [])

  const requestResolve = useCallback((hit) => {
    if (!hit?.sourceId) return
    if (resolvedIds.has(hit.sourceId)) {
      toggleResolved(hit.sourceId)
      return
    }
    setResolveConfirm({
      sourceId: hit.sourceId,
      label: hit.label,
      action: hit.action,
      resolvedWhen: hit.resolvedWhen,
      count: hit.count,
    })
  }, [resolvedIds, toggleResolved])

  const cancelResolveConfirm = useCallback(() => {
    setResolveConfirm(null)
  }, [])

  const confirmResolve = useCallback(() => {
    if (!resolveConfirm?.sourceId) return
    setResolvedIds((prev) => {
      const next = new Set(prev)
      next.add(resolveConfirm.sourceId)
      return next
    })
    setResolveConfirm(null)
  }, [resolveConfirm])

  const preloadCase = useCallback(() => {
    if (!onCreateCase) return
    onCreateCase({
      rut: plan?.anchors?.resolvedRut || selectedCompany?.rut || '',
      email: form.email || plan?.anchors?.resolvedEmail || '',
      companyId:
        selectedCompanyId && selectedCompanyId !== ALL_COMPANIES
          ? selectedCompanyId
          : plan?.anchors?.companyIds?.[0] || '',
      userId: form.userId || plan?.anchors?.userIds?.[0] || '',
      name: form.name || plan?.subject?.name || '',
    })
  }, [form, plan, selectedCompanyId, selectedCompany, onCreateCase])

  useEffect(() => {
    logEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [logs])

  return (
    <div className="privacy-subject-search">
      <p className="privacy-subject-lead">
        Indica nombre, correo o ID del usuario. La evaluación arma un checklist
        de lo que hay que revisar, revocar o transferir antes de dar de baja su
        perfil en la empresa, sin dejar accesos abiertos ni avisos a un buzón
        que ya no se usa.
      </p>

      <form className="privacy-subject-search-form" onSubmit={runSearch}>
        <div className="privacy-subject-search-grid privacy-subject-search-grid--compact">
          <label>
            <span>Nombre</span>
            <input
              type="text"
              value={form.name}
              onChange={setName}
              placeholder="Nombre y/o apellido"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </label>
          <label>
            <span>Correo</span>
            <input
              type="email"
              value={form.email}
              onChange={setEmail}
              placeholder="usuario@empresa.cl"
              autoComplete="off"
              disabled={busy}
            />
          </label>
          <label>
            <span>ID del usuario</span>
            <input
              type="text"
              value={form.userId}
              onChange={setUserId}
              placeholder="ID en Mongo (users._id)"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </label>
        </div>

        {localError ? <div className="privacy-note error">{localError}</div> : null}

        <div className="privacy-subject-search-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            <SearchIcon />
            {busy ? 'Evaluando…' : 'Evaluar baja del usuario'}
          </button>
        </div>
      </form>

      {plan && awaitingUserPick ? (
        <div className="privacy-subject-user-pick" role="group" aria-label="Elegir usuario">
          <h4>
            {(plan.users || []).length > 1
              ? 'Varios usuarios coinciden'
              : 'Confirma el usuario'}
          </h4>
          <p className="privacy-subject-user-pick-lead">
            {(plan.users || []).length > 1
              ? 'Hay más de una cuenta con ese criterio. Elige cuál vas a revisar antes de continuar.'
              : 'El criterio no identificó un solo usuario con certeza. Confirma cuál cuenta evaluar.'}
          </p>
          <ul className="privacy-subject-user-pick-list">
            {(plan.users || []).map((u) => {
              const selected = pickedUserId === u.id
              return (
                <li key={u.id}>
                  <label
                    className={
                      selected
                        ? 'privacy-subject-user-pick-card is-selected'
                        : 'privacy-subject-user-pick-card'
                    }
                  >
                    <input
                      type="radio"
                      name="privacy-subject-user"
                      value={u.id}
                      checked={selected}
                      onChange={() => setPickedUserId(u.id)}
                      disabled={busy}
                    />
                    <span className="privacy-subject-user-pick-body">
                      <span className="privacy-subject-user-pick-name">
                        {u.name || 'Sin nombre'}
                      </span>
                      <span className="privacy-subject-user-pick-meta">
                        {[u.email, u.rut, u.adminStatus, (u.roles || []).join(', ')]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      <span className="privacy-subject-user-pick-id">
                        ID en Mongo: {u.id}
                      </span>
                      {u.matchReason ? (
                        <span className="privacy-subject-user-pick-reason">
                          Por:{' '}
                          {MATCH_REASON_LABEL[u.matchReason] || u.matchReason}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
          <div className="privacy-subject-search-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !pickedUserId}
              onClick={confirmPickedUser}
            >
              Continuar con este usuario
            </button>
          </div>
        </div>
      ) : null}

      {plan && !awaitingUserPick ? (
        <div className="privacy-subject-titular">
          <h4>Usuario en evaluación</h4>
          {(() => {
            const identified =
              (plan.users || []).find(
                (u) =>
                  u.id === pickedUserId ||
                  u.id === plan.subject?.userId,
              ) ||
              (plan.users || [])[0] ||
              null
            if (!identified && !plan.subject?.userId) {
              return (
                <p className="privacy-subject-titular-hint">
                  Sin documento en <code>users</code> para este criterio
                  {[
                    plan.subject?.rut || plan.anchors?.resolvedRut,
                    plan.subject?.email || plan.anchors?.resolvedEmail,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                    ? ` (${[
                        plan.subject?.rut || plan.anchors?.resolvedRut,
                        plan.subject?.email || plan.anchors?.resolvedEmail,
                      ]
                        .filter(Boolean)
                        .join(' · ')})`
                    : ''}
                  .
                </p>
              )
            }
            const name =
              identified?.name || plan.subject?.name || 'Sin nombre'
            const email =
              identified?.email ||
              plan.subject?.email ||
              plan.anchors?.resolvedEmail
            const rut =
              identified?.rut ||
              plan.subject?.rut ||
              plan.anchors?.resolvedRut
            const mongoId =
              identified?.id || plan.subject?.userId || null
            return (
              <div className="privacy-subject-titular-card">
                <div className="privacy-subject-titular-name">{name}</div>
                <div className="privacy-subject-titular-meta">
                  {[email, rut, identified?.adminStatus]
                    .filter(Boolean)
                    .join(' · ')}
                  {(identified?.roles || []).length
                    ? ` · ${(identified.roles || []).join(', ')}`
                    : ''}
                </div>
                {mongoId ? (
                  <div className="privacy-subject-titular-id">
                    <span className="privacy-subject-titular-id-label">
                      ID en Mongo
                    </span>
                    <code title="users._id para buscar en la base">{mongoId}</code>
                  </div>
                ) : null}
                {identified?.matchReason ? (
                  <p className="privacy-subject-titular-hint">
                    Coincide por:{' '}
                    {MATCH_REASON_LABEL[identified.matchReason] ||
                      identified.matchReason}
                  </p>
                ) : null}
              </div>
            )
          })()}
          {plan.companies?.length ? (
            <p className="privacy-subject-titular-hint">
              {plan.companies.length} empresa
              {plan.companies.length === 1 ? '' : 's'} con vínculo · solo se
              listan hallazgos del usuario (no bancos, DTE ni cartola de la empresa)
            </p>
          ) : null}
        </div>
      ) : null}

      {progress && !awaitingUserPick ? (
        <div className="privacy-subject-progress" aria-live="polite">
          <div className="privacy-subject-progress-head">
            <span>
              {busy
                ? `Revisando ${progress.current} / ${progress.total}`
                : 'Evaluación completada'}
            </span>
            <span className="privacy-subject-progress-pct">{percent}%</span>
          </div>
          <div className="privacy-subject-progress-track">
            <div
              className="privacy-subject-progress-bar"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="privacy-subject-progress-label">{progress.label}</div>
          <div className="privacy-subject-log" role="log">
            {logs.map((entry) => (
              <div
                key={entry.id}
                className={`privacy-subject-log-line is-${entry.tone}`}
              >
                {entry.message}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      ) : null}

      {scanDone && !awaitingUserPick && plan ? (
        <div className="privacy-subject-search-results">
          <h2 className="privacy-subject-results-title">Checklist de baja</h2>
          <div className="privacy-subject-results-toolbar">
            <div className="privacy-subject-search-summary">
              <div>
                <strong>{openItems.length}</strong>
                {openItems.length === 1
                  ? ' punto por confirmar'
                  : ' puntos por confirmar'}
                {blockingOpen.length ? (
                  <span className="privacy-subject-search-identity">
                    {' '}
                    · {blockingOpen.length} bloqueante
                    {blockingOpen.length === 1 ? '' : 's'}
                  </span>
                ) : null}
                {scopeLabel ? (
                  <span className="privacy-subject-search-identity">
                    {' '}
                    · {scopeLabel}
                  </span>
                ) : null}
              </div>
              {onCreateCase ? (
                <button
                  type="button"
                  className="btn btn-outline privacy-subject-results-action"
                  onClick={preloadCase}
                  title="Abrir formulario de solicitud DSAR con estos datos"
                >
                  Abrir solicitud DSAR
                </button>
              ) : null}
            </div>

            {plan?.companies?.length ? (
              <div
                className="privacy-subject-scope"
                role="tablist"
                aria-label="Alcance por empresa"
              >
                <span className="privacy-subject-scope-label">Empresa</span>
                <div className="privacy-subject-scope-chips">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={selectedCompanyId === ALL_COMPANIES}
                    className={
                      selectedCompanyId === ALL_COMPANIES
                        ? 'privacy-subject-scope-chip is-active'
                        : 'privacy-subject-scope-chip'
                    }
                    onClick={() => setSelectedCompanyId(ALL_COMPANIES)}
                  >
                    Todas
                    <span className="privacy-subject-scope-chip-meta">
                      {plan.companies.length}
                    </span>
                  </button>
                  {plan.companies.map((c) => {
                    const active = selectedCompanyId === c.id
                    const reason = MATCH_REASON_LABEL[c.matchReason]
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        title={[c.name || c.id, c.rut, reason]
                          .filter(Boolean)
                          .join(' · ')}
                        className={
                          active
                            ? 'privacy-subject-scope-chip is-active'
                            : 'privacy-subject-scope-chip'
                        }
                        onClick={() => setSelectedCompanyId(c.id)}
                      >
                        <span className="privacy-subject-scope-chip-name">
                          {c.name || c.id}
                        </span>
                        {c.rut ? (
                          <span className="privacy-subject-scope-chip-meta">
                            {c.rut}
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={
              canOffboard
                ? 'privacy-subject-verdict is-ready'
                : 'privacy-subject-verdict is-blocked'
            }
          >
            {canOffboard ? (
              <p>
                <strong>Listo para baja.</strong>{' '}
                {checklistItems.length
                  ? 'Confirmaste todos los puntos del usuario en el alcance actual.'
                  : 'No apareció nada del usuario que requiera acción en este alcance.'}{' '}
                Los datos operativos de la empresa (bancos, DTE, cartola) no
                entran en este checklist.
              </p>
            ) : (
              <p>
                <strong>Aún no está listo.</strong> Revisa cada punto con
                hallazgo y márcalo como resuelto cuando hayas hecho la acción
                indicada.
              </p>
            )}
          </div>

          {!hits.length ? (
            <p className="privacy-subject-empty">
              La evaluación terminó sin filas en el checklist. Revisa el registro
              de la evaluación arriba por errores o criterios sin coincidencia.
            </p>
          ) : null}

          {hits.length && !byCategory.length ? (
            <p className="privacy-subject-empty">
              En este alcance no hay puntos del usuario que requieran acción. Lo
              demás pertenece a la empresa y no forma parte de esta baja.
            </p>
          ) : null}

          {byCategory.map((section) => (
            <section key={section.key} className="privacy-subject-layer">
              <div className="privacy-subject-layer-head">
                <h3>{section.title}</h3>
                <span className="privacy-subject-layer-meta">
                  {section.openCount
                    ? `${section.openCount} por confirmar`
                    : 'completo'}
                  {' · '}
                  {section.total} punto{section.total === 1 ? '' : 's'}
                </span>
              </div>
              <ul className="privacy-subject-checklist">
                {section.rows.map((hit) => {
                  const resolved = resolvedIds.has(hit.sourceId)
                  const status = statusOf(hit, resolved)
                  const attention = needsAttention(hit)
                  const compact = status === 'clear' || status === 'done'
                  const samples = hit.samples || []
                  const extraCount =
                    typeof hit.count === 'number' && hit.count > samples.length
                      ? hit.count - samples.length
                      : 0

                  return (
                    <li
                      key={hit.sourceId}
                      className={`privacy-subject-check-item is-${status}${
                        compact ? ' is-compact' : ''
                      }`}
                    >
                      <div className="privacy-subject-check-main">
                        <div className="privacy-subject-check-head">
                          <input
                            type="checkbox"
                            disabled={!attention}
                            checked={
                              resolved || (!attention && hit.count === 0)
                            }
                            onChange={() => {
                              if (!attention) return
                              requestResolve(hit)
                            }}
                            aria-label={`Marcar resuelto: ${hit.label}`}
                          />
                          <span className="privacy-subject-check-title-block">
                            <span className="privacy-subject-check-title">
                              {hit.label}
                            </span>
                            {hit.collection || hit.externalSystem ? (
                              <span className="privacy-subject-check-source">
                                {hit.collection || hit.externalSystem}
                                {hit.system ? ` · ${hit.system}` : ''}
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={`privacy-subject-check-badge is-${status}`}
                          >
                            {STATUS_LABEL[status]}
                            {typeof hit.count === 'number' && hit.count > 0
                              ? ` · ${hit.count}`
                              : ''}
                          </span>
                        </div>

                        {!compact && hit.action ? (
                          <p className="privacy-subject-check-action">
                            {hit.action}
                          </p>
                        ) : null}

                        {!compact &&
                        (hit.system === 'S3' ||
                          hit.sourceId === 'downloads' ||
                          samples.some((s) =>
                            String(s.recordId || '').startsWith('s3://'),
                          )) &&
                        samples.length ? (
                          <div className="privacy-subject-s3-paths">
                            <div className="privacy-subject-s3-paths-head">
                              Rutas en S3 (archivos del usuario)
                            </div>
                            <ul className="privacy-subject-s3-paths-list">
                              {samples.map((s) => (
                                <li key={s.recordId}>
                                  <span className="privacy-subject-s3-paths-label">
                                    {s.label || 'Prefijo'}
                                    {s.companyId
                                      ? ` · ${
                                          companyNameById[s.companyId] ||
                                          s.companyId
                                        }`
                                      : ''}
                                  </span>
                                  <S3PathUri uri={s.recordId} />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {!compact &&
                        samples.length &&
                        hit.system !== 'S3' &&
                        hit.sourceId !== 'downloads' &&
                        !samples.some((s) =>
                          String(s.recordId || '').startsWith('s3://'),
                        ) ? (
                          <div className="privacy-subject-findings">
                            <div className="privacy-subject-findings-head">
                              Hallazgos a revisar
                              {typeof hit.count === 'number' ? (
                                <span> · {hit.count}</span>
                              ) : null}
                            </div>
                            <table className="privacy-subject-findings-table">
                              <thead>
                                <tr>
                                  <th>Detalle</th>
                                  <th>Empresa</th>
                                  <th>Id</th>
                                </tr>
                              </thead>
                              <tbody>
                                {samples.map((s) => {
                                  const parts = parseSampleParts(s.label)
                                  const companyLabel =
                                    (s.companyId &&
                                      companyNameById[s.companyId]) ||
                                    s.companyId ||
                                    '—'
                                  return (
                                    <tr key={s.recordId}>
                                      <td>
                                        <div className="privacy-subject-finding-primary">
                                          {parts[0] || s.recordId}
                                        </div>
                                        {parts.length > 1 ? (
                                          <div className="privacy-subject-finding-secondary">
                                            {parts.slice(1).join(' · ')}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td>{companyLabel}</td>
                                      <td>
                                        <code>{s.recordId}</code>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                            {extraCount > 0 ? (
                              <p className="privacy-subject-findings-more">
                                +{extraCount} registro
                                {extraCount === 1 ? '' : 's'} más no mostrados
                                en la muestra
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        {!compact ? (
                          <details className="privacy-subject-check-criteria">
                            <summary>
                              <span className="privacy-subject-check-criteria-toggle" aria-hidden>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.25"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="m6 9 6 6 6-6" />
                                </svg>
                              </span>
                              <span>Qué revisar y cuándo está listo</span>
                            </summary>
                            <div className="privacy-subject-check-criteria-body">
                              {hit.verify ? (
                                <div className="privacy-subject-check-criteria-row">
                                  <span className="privacy-subject-check-criteria-label">
                                    Qué comprobar
                                  </span>
                                  <p>{hit.verify}</p>
                                </div>
                              ) : null}
                              {hit.risk ? (
                                <div className="privacy-subject-check-criteria-row">
                                  <span className="privacy-subject-check-criteria-label">
                                    Si no se atiende
                                  </span>
                                  <p>{hit.risk}</p>
                                </div>
                              ) : null}
                              {hit.resolvedWhen ? (
                                <div className="privacy-subject-check-criteria-row">
                                  <span className="privacy-subject-check-criteria-label">
                                    Listo cuando
                                  </span>
                                  <p>{hit.resolvedWhen}</p>
                                </div>
                              ) : null}
                            </div>
                          </details>
                        ) : hit.verify ? (
                          <p className="privacy-subject-check-compact-hint">
                            {hit.verify}
                          </p>
                        ) : null}

                        {!compact && hit.note ? (
                          <pre className="privacy-subject-check-note">
                            {hit.note}
                          </pre>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : null}

      {resolveConfirm ? (
        <div
          className="privacy-subject-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-subject-confirm-title"
          onClick={cancelResolveConfirm}
        >
          <div
            className="privacy-subject-confirm-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="privacy-subject-confirm-title">
              Confirmar punto resuelto
            </h3>
            <p className="privacy-subject-confirm-lead">
              Confirmo que este punto está resuelto y ya no bloquea la baja del
              usuario en la empresa.
            </p>
            <div className="privacy-subject-confirm-item">
              <strong>{resolveConfirm.label}</strong>
              {typeof resolveConfirm.count === 'number' &&
              resolveConfirm.count > 0 ? (
                <span>· {resolveConfirm.count} hallazgo
                  {resolveConfirm.count === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            {resolveConfirm.resolvedWhen ? (
              <div className="privacy-subject-confirm-criterion">
                <span>Listo cuando</span>
                <p>{resolveConfirm.resolvedWhen}</p>
              </div>
            ) : null}
            {resolveConfirm.action ? (
              <div className="privacy-subject-confirm-criterion">
                <span>Qué se debió hacer</span>
                <p>{resolveConfirm.action}</p>
              </div>
            ) : null}
            <div className="privacy-subject-confirm-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={cancelResolveConfirm}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmResolve}
              >
                Sí, está resuelto
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default PrivacySubjectSearch
